// Pantalla propia del comprobante de domicilio (paso 02). Va después del veredicto de identidad y
// antes de las preguntas, solo cuando el documento no trajo domicilio.
import { useState } from 'react';
import { PencilLine, ReceiptText } from 'lucide-react';
import { KycAttemptStageEnum } from '@fiado/type-kit/bin/retailWizard/enums/KycAttemptStageEnum';
import { Button } from '@/kit';
import { toast } from 'sonner';
import { cn } from '@/lib/cn';
import { useDebounce } from '@/shared/hooks/useDebounce';
import type { MetamapAttempt } from '../api/useMetamapAttempt';
import { useAddressSearch, useConfirmAddress } from '../api/useWizard';
import { wizardErrorMsg } from '../api/wizardError';
import { attemptInFlight } from '../metamapAttempt';
import {
    proofOfAddressFeedback,
    proofOfAddressRetryLabel,
    proofStillOpen,
    type ProofAddressTone,
} from '../proofOfAddress';
import { step02RequestCopy } from '../step02Request';
import type { AddressCandidate, ProofOfAddressResult } from '../types';
import { AddressCombobox, AddressSaveConfirm } from './AddressCombobox';
import { ModalityPicker, ScreenIntro, WaitingIcon } from './KycScreenBits';
import { AttemptSdkModal, MetamapAttemptPanel, SwitchConfirmDialog } from './MetamapAttemptPanel';

const COPY = step02RequestCopy('PROOF_OF_ADDRESS');

export interface ProofOfAddressScreenProps {
    sessionId: string;
    attempt: MetamapAttempt;
    /** Veredicto del intento vigente, `null` mientras no llega. */
    result: ProofOfAddressResult | null;
    /** El silo sigue exigiendo domicilio: decide si hay botón de reintentar. */
    addressRequired: boolean;
    /** Repregunta ya por el requisito; así sale de aquí el domicilio capturado a mano. */
    onRecheck: () => void | Promise<void>;
}

type AddressMode = 'photo' | 'manual';

/** Lo tecleado a mano; vive en la pantalla para no perderse al ir y venir de la foto. */
interface ManualDraft {
    query: string;
    chosen: AddressCandidate | null;
    /** Terminal: guardado, ya no se puede volver ni guardar otro. */
    saved: boolean;
}

const EMPTY_DRAFT: ManualDraft = { query: '', chosen: null, saved: false };

/**
 * A mano solo sin comprobante en camino: antes de que el cliente empiece, vencido o con veredicto.
 * Con el cliente subiendo el recibo, el webhook guardaría otro domicilio.
 */
function manualEntryAllowed(stage: KycAttemptStageEnum | null, inReview: boolean): boolean {
    if (inReview) return false;
    return stage !== KycAttemptStageEnum.STARTED && stage !== KycAttemptStageEnum.INPUTS_COMPLETED;
}

/** El cuerpo de la pantalla; la `KycCard` la pone el paso. */
export function ProofOfAddressScreen({
    sessionId,
    attempt,
    result,
    addressRequired,
    onRecheck,
}: ProofOfAddressScreenProps) {
    const [mode, setMode] = useState<AddressMode>('photo');
    const [draft, setDraft] = useState<ManualDraft>(EMPTY_DRAFT);
    // Intento abierto o vencido: manda el panel. En revisión del proveedor: aviso sin QR.
    const attemptOpen =
        attemptInFlight(attempt.stage) || attempt.stage === KycAttemptStageEnum.EXPIRED;
    const review = !attemptOpen && result !== null && proofStillOpen(result) ? result : null;
    const manualAllowed = draft.saved || manualEntryAllowed(attempt.stage, review !== null);
    // Si se bloquea la captura a mano, regresa a la foto para no saltar sola a «Escribirlo» después.
    if (mode === 'manual' && !manualAllowed) setMode('photo');
    const manual = mode === 'manual' && manualAllowed;

    const goManual = (): void => {
        // El SDK abierto se suelta: el cliente ya no va a fotografiar nada.
        if (attempt.sdk !== null) attempt.closeSdk();
        setMode('manual');
    };

    return (
        <div className="text-center">
            {!draft.saved && (
                <AddressModeToggle
                    manual={manual}
                    manualAllowed={manualAllowed}
                    onPhoto={() => {
                        setMode('photo');
                    }}
                    onManual={goManual}
                />
            )}

            {manual ? (
                <ManualAddressEntry
                    sessionId={sessionId}
                    draft={draft}
                    onDraftChange={setDraft}
                    onSaved={onRecheck}
                    onBackToPhoto={() => {
                        setMode('photo');
                    }}
                />
            ) : (
                <>
                    {attemptOpen ? (
                        <MetamapAttemptPanel attempt={attempt} copy={COPY} />
                    ) : review ? (
                        <ReviewScreen attempt={attempt} result={review} />
                    ) : (
                        <>
                            <PickScreen
                                attempt={attempt}
                                result={result}
                                addressRequired={addressRequired}
                            />
                            <ConfigUnavailableNotice show={attempt.configUnavailable} />
                        </>
                    )}
                    {manualAllowed && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="mt-1 text-[11.5px] text-muted-foreground"
                            disabled={attempt.isPending}
                            onClick={goManual}
                        >
                            <PencilLine className="mr-1.5 size-3.5" aria-hidden />
                            No trae comprobante · Escribir el domicilio
                        </Button>
                    )}
                </>
            )}

            {/* Fuera del cambio de estado: abrir el intento no debe remontar el SDK. */}
            <AttemptSdkModal attempt={attempt} title={COPY.modalTitle} />
            <SwitchConfirmDialog attempt={attempt} />
            <PrepareError error={attempt.error} />
        </div>
    );
}

/** Selector segmentado entre la foto del comprobante y escribir el domicilio. */
function AddressModeToggle({
    manual,
    manualAllowed,
    onPhoto,
    onManual,
}: {
    manual: boolean;
    manualAllowed: boolean;
    onPhoto: () => void;
    onManual: () => void;
}) {
    const segment = (active: boolean): string =>
        cn(
            'rounded-md px-3 py-1.5 text-[12px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50',
            active ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground',
        );
    return (
        <div className="mb-5">
            <div
                role="group"
                aria-label="Cómo registrar el domicilio"
                className="inline-flex gap-1 rounded-lg bg-muted p-1"
            >
                <button
                    type="button"
                    aria-pressed={!manual}
                    className={segment(!manual)}
                    onClick={onPhoto}
                >
                    Foto del comprobante
                </button>
                <button
                    type="button"
                    aria-pressed={manual}
                    disabled={!manualAllowed}
                    className={segment(manual)}
                    onClick={onManual}
                >
                    Escribirlo
                </button>
            </div>
            {!manualAllowed && (
                <p className="mt-2 text-[11.5px] text-muted-foreground">
                    El cliente ya está mandando su comprobante.
                </p>
            )}
        </div>
    );
}

/**
 * La salida para el cliente sin recibo a su nombre: teclea su domicilio con el buscador de AWS Place
 * y se guarda en el expediente por la misma vía que el del INE, así que el paso se libera igual.
 */
function ManualAddressEntry({
    sessionId,
    draft,
    onDraftChange,
    onSaved,
    onBackToPhoto,
}: {
    sessionId: string;
    draft: ManualDraft;
    onDraftChange: (draft: ManualDraft) => void;
    /** Repregunta por el requisito para que la pantalla se cierre sin esperar al poll. */
    onSaved: () => void | Promise<void>;
    onBackToPhoto: () => void;
}) {
    const confirmAddress = useConfirmAddress(sessionId);
    const { query, chosen } = draft;
    // Sin debounce, cada tecla a partir de la tercera era una llamada a AWS Place.
    const debouncedQuery = useDebounce(query.trim(), 350);
    const {
        data: candidates,
        isFetching: searching,
        isError: searchFailed,
        refetch: retrySearch,
    } = useAddressSearch(sessionId, debouncedQuery);

    const save = (candidate: AddressCandidate): void => {
        // El fallo del guardado y el de la repregunta se manejan aparte: con un `catch` común, un
        // poll que rebota diría que no se pudo guardar un domicilio que ya está en el expediente.
        void confirmAddress.mutateAsync({ useIne: false, address: candidate.domicilio }).then(
            () => {
                onDraftChange({ query: '', chosen: null, saved: true });
                toast.success('Domicilio guardado');
                void Promise.resolve(onSaved()).catch(() => undefined);
            },
            (e: unknown) => {
                toast.error(
                    wizardErrorMsg(e, 'No se pudo guardar el domicilio — intenta de nuevo.'),
                );
            },
        );
    };

    // Terminal a propósito: la pantalla sigue montada hasta que el poll apague `addressRequired`.
    if (draft.saved) {
        return (
            <div
                role="status"
                className="mx-auto mt-4 max-w-md rounded-md border border-success-soft-border bg-success-soft px-3 py-2 text-left text-success"
            >
                <p className="text-[12.5px] font-semibold">Domicilio guardado</p>
                <p className="mt-1 text-[11.5px] leading-relaxed opacity-90">
                    Ya se registró en el expediente del cliente. Estamos saliendo de esta pantalla.
                </p>
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-md text-left">
            <p className="text-[12px] leading-[1.45] text-muted-foreground">
                Busca la calle y el número y elige dónde vive; solo hay domicilios de México. Puedes
                volver a la foto cuando quieras.
            </p>

            <div className="mt-3">
                <AddressCombobox
                    label="Buscar domicilio"
                    focusOnMount
                    query={query}
                    onQueryChange={(value) => {
                        // Al cambiar la búsqueda, lo elegido deja de tener que ver con lo que se ve.
                        onDraftChange({ ...draft, query: value, chosen: null });
                    }}
                    debouncedQuery={debouncedQuery}
                    searching={searching}
                    candidates={candidates ?? []}
                    searchFailed={searchFailed}
                    onRetrySearch={() => void retrySearch()}
                    chosen={chosen}
                    onChoose={(candidate) => {
                        onDraftChange({ ...draft, chosen: candidate });
                    }}
                />

                {chosen && (
                    <AddressSaveConfirm
                        label={chosen.etiqueta}
                        saving={confirmAddress.isPending}
                        onSave={() => {
                            save(chosen);
                        }}
                        onDiscard={() => {
                            onDraftChange({ ...draft, chosen: null });
                        }}
                    />
                )}

                <Button
                    variant="ghost"
                    size="sm"
                    className="mt-3 w-full sm:w-auto"
                    disabled={confirmAddress.isPending}
                    onClick={onBackToPhoto}
                >
                    ← Mejor con foto del comprobante
                </Button>
            </div>
        </div>
    );
}

/** Revisión manual del proveedor: puede durar horas, así que no se muestra el QR. */
function ReviewScreen({
    attempt,
    result,
}: {
    attempt: MetamapAttempt;
    result: ProofOfAddressResult;
}) {
    const feedback = proofOfAddressFeedback(result.status);
    return (
        <>
            <WaitingIcon />
            <p className="mb-1 text-[15px] font-semibold">Revisando el comprobante…</p>
            <p
                role="status"
                aria-live="polite"
                className="mx-auto mb-5 max-w-md text-[12.5px] leading-relaxed text-muted-foreground"
            >
                El comprobante ya llegó y lo están revisando. En cuanto haya resultado aparece aquí.
            </p>
            <div className="mx-auto mb-5 max-w-md text-left">
                <ProofNotice tone={feedback.tone} title={feedback.title} detail={feedback.detail} />
            </div>

            {attempt.timedOut && (
                <div className="mx-auto mb-5 max-w-md">
                    <p className="mb-2 text-[11.5px] leading-relaxed text-muted-foreground">
                        Esto ya tardó más de lo normal. Consulta ahora o pide otro comprobante.
                    </p>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                            void attempt.checkNow();
                        }}
                    >
                        Consultar ahora
                    </Button>
                </div>
            )}

            <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                Pedir otro comprobante
            </p>
            <ModalityPicker
                tabletHint={COPY.tabletHint}
                phoneHint={COPY.phoneHint}
                loading={attempt.isPending}
                onPick={attempt.launch}
            />
        </>
    );
}

/** Qué se pide y cómo capturarlo, más el veredicto anterior cuando lo hubo. */
function PickScreen({
    attempt,
    result,
    addressRequired,
}: {
    attempt: MetamapAttempt;
    result: ProofOfAddressResult | null;
    addressRequired: boolean;
}) {
    return (
        <>
            <ScreenIntro
                tone="warning"
                icon={<ReceiptText className="size-7" aria-hidden />}
                title={COPY.pickTitle}
                sub={COPY.pickSub}
            />

            {result ? (
                <ProofVerdict result={result} attempt={attempt} addressRequired={addressRequired} />
            ) : (
                <ModalityPicker
                    tabletHint={COPY.tabletHint}
                    phoneHint={COPY.phoneHint}
                    loading={attempt.isPending}
                    onPick={attempt.launch}
                />
            )}
        </>
    );
}

/** Qué pasó con el comprobante y qué sigue; sin tope de reintentos mientras el silo pida domicilio. */
function ProofVerdict({
    result,
    attempt,
    addressRequired,
}: {
    result: ProofOfAddressResult;
    attempt: MetamapAttempt;
    addressRequired: boolean;
}) {
    const feedback = proofOfAddressFeedback(result.status);
    const retryLabel = proofOfAddressRetryLabel(feedback.outcome, addressRequired);

    return (
        <div className="mx-auto mb-5 max-w-md text-left">
            <ProofNotice tone={feedback.tone} title={feedback.title} detail={feedback.detail} />

            {retryLabel && (
                <>
                    <p className="mb-2 mt-4 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                        {retryLabel}
                    </p>
                    <ModalityPicker
                        tabletHint={COPY.tabletHint}
                        phoneHint={COPY.phoneHint}
                        loading={attempt.isPending}
                        onPick={attempt.launch}
                    />
                </>
            )}
        </div>
    );
}

function ProofNotice({
    tone,
    title,
    detail,
}: {
    tone: ProofAddressTone;
    title: string;
    detail: string;
}) {
    return (
        <div
            role="status"
            className={cn(
                'rounded-md border px-3 py-2 text-left',
                tone === 'success' && 'border-success-soft-border bg-success-soft text-success',
                tone === 'warning' && 'border-warning-soft-border bg-warning-soft text-warning',
                tone === 'danger' && 'border-destructive/30 bg-destructive/10 text-destructive',
            )}
        >
            <p className="text-[12.5px] font-semibold">{title}</p>
            <p className="mt-1 text-[11.5px] leading-relaxed opacity-90">{detail}</p>
        </div>
    );
}

function ConfigUnavailableNotice({ show }: { show: boolean }) {
    if (!show) return null;
    return (
        <div className="mx-auto mb-4 max-w-md rounded-md border border-warning-soft-border bg-warning-soft px-3 py-2 text-left text-[12px] text-warning">
            {COPY.configUnavailable}
        </div>
    );
}

function PrepareError({ error }: { error: unknown }) {
    if (!error) return null;
    return (
        <div className="mx-auto mb-4 max-w-md rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-left text-[12px] text-destructive">
            No se pudo pedir el comprobante de domicilio. Revisa la conexión e intenta de nuevo.
        </div>
    );
}
